import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreHotelsComponent } from "@b2bportal/core-types";
import { HotelSteps, NavigationFlow } from "@b2bportal/core-utilities";
import {
  HotelAvailabilityTitle,
  HotelListLoading,
  HotelList,
  HotelListSort,
  Filters,
  HotelAvailabilityMap,
  AvailabilityMapDialog,
} from "@tenant/hotels";
import { Icon, Page } from "@tenant/ui";
import clsx from "clsx";
import { useHotelsConfig } from "../../../../context";
import { useHotelSlot } from "../../../../slots";
import defaultStyles from "./AvailabilityPage.module.scss";
import type { AvailabilityPageProps } from "./types";

const COMPONENT_KEY = CoreHotelsComponent.AvailabilityPage;

export const AvailabilityPage = ({
  context,
  handlers,
  children,
  className,
  ...pageProps
}: AvailabilityPageProps) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const HotelSlot = useHotelSlot();
  const { enableStayTypeFilter = false } = useHotelsConfig();

  const currentStep =
    NavigationFlow.useGetCurrentStep<HotelSteps>() ?? HotelSteps.AVAILABILITY;

  return (
    <Page className={clsx(block, className)} {...pageProps}>
      <div className={cn("container")}>
        <div className={cn("hotel-action-container")}>
          <HotelSlot.HotelBreadcrumbs
            className={cn("breadcrumbs")}
            currentStep={currentStep}
          />
          <div className={cn("search")}>
            <HotelSlot.HotelAvailabilitySearch />
          </div>
          <div className={cn("filter")}>
            <Filters />
          </div>
          {enableStayTypeFilter && <HotelSlot.StayTypeFilter />}
          {context.isReady ? (
            <div className={cn("hotel-header-container")}>
              <div className={cn("title-container")}>
                <HotelAvailabilityTitle />
              </div>
              <div className={cn("sort")}>
                <HotelListSort />
              </div>
            </div>
          ) : null}
        </div>

        <div className={cn("main-container")}>
          {context.isReady && (
            <div className={cn("result-container")}>
              {context.isError ? (
                <HotelSlot.HotelListError className={cn("error")} />
              ) : (
                <div className={cn("list-container")}>
                  <HotelSlot.HotelAvailabilityBanner />
                  <HotelList onHover={handlers.setHoveredId} />
                </div>
              )}
            </div>
          )}
        </div>

        <div className={cn("map-container")}>
          <HotelAvailabilityMap
            loading={context.isLoading}
            hoveredId={context.hoveredId}
            hotels={context.hotelsList}
            centroid={context.mapCenter}
            zoom={context.zoom}
          />
        </div>

        <button
          className={cn("shop-mode-button", [
            context.shopModeConfig.className ?? "",
          ])}
          onClick={() => context.shopModeConfig.onClick()}
        >
          {context.shopModeConfig.text}
          <Icon iconName={context.shopModeConfig.icon} />
        </button>

        <HotelListLoading
          isLoading={context.isLoading || context.isInitializing}
        />

        <AvailabilityMapDialog
          dialogProps={context.mapDialog.props}
          mapProps={{
            loading: context.isLoading,
            hoveredId: context.hoveredId,
            hotels: context.hotelsList,
            centroid: context.mapCenter,
            zoom: context.zoom,
          }}
        />

        {children}
      </div>
    </Page>
  );
};
